import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../dataManagement/uiDb/UiDb";
import { useMediaUpload } from "./useMediaUpload";
import { makeBusinessFieldPersist } from "../services/helpers/BusinessFieldPersistHelper";

export interface UseBusinessLogoUploadReturn {
  /**
   * Uploads the selected file through the presigned-storage pipeline (same
   * engine as the gallery — see useMediaUpload), then persists the resulting
   * publicUrl onto the business via updateBusiness({ logoUrl }). Resolves to
   * the permanent publicUrl on success. Throws an Error with a
   * user-friendly message on failure — callers should catch and surface via
   * their notification system.
   */
  upload: (file: File) => Promise<string>;
  isUploading: boolean;
}

/**
 * Logo upload for a business — reuses the generic presigned-storage upload
 * engine (useMediaUpload), same as useBusinessGallery/useBusinessBannerUpload.
 * The uploaded record isn't appended to any list; its publicUrl is persisted
 * directly onto the business (logoUrl) via the updateBusiness thunk. That
 * thunk's reducer already updates Redux's currentBusiness/myBusinesses on
 * success, so the new logo is live the moment `upload` resolves — no local
 * override state needed on the caller's side.
 *
 * For the create-business flow, where the businessId isn't known until after
 * the business itself is created, use useMediaUpload directly instead (see
 * CreateBusinessModal) — this hook is for callers that already have a fixed
 * businessId (e.g. the inline logo editor on the business page).
 */
export function useBusinessLogoUpload(businessId: string | undefined): UseBusinessLogoUploadReturn {
  const dispatch = useDispatch<AppDispatch>();

  // Persist step handed to the generic upload engine: sets logoUrl on the
  // business itself, rather than a gallery append.
  const persist = useCallback(
    makeBusinessFieldPersist(
      dispatch,
      "logoUrl",
      () => businessId,
      "Uploaded, but couldn't save the logo. Please try again."
    ),
    [dispatch, businessId]
  );

  const { isUploading, upload: uploadFiles } = useMediaUpload({ folder: "business", persist });

  const upload = useCallback(
    async (file: File): Promise<string> => {
      if (!businessId) throw new Error("Business not ready yet.");
      const batch = await uploadFiles([file]);
      const result = batch.results[0];
      if (!result || result.status !== "success") {
        throw new Error(result?.message ?? "Failed to upload logo image.");
      }
      return result.record.publicUrl;
    },
    [businessId, uploadFiles]
  );

  return { upload, isUploading };
}
